import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { handleSessionExpiration } from "./sessionManagement";
import { Labels } from "../components/sharedComponents/PatientLabels";
import {
  Survey,
  SurveyResponse,
  SurveyState,
  SurveyTimeWindow,
} from "../types/surveyTypes";
import { ReportList } from "../types/reportTypes";
import { formatLocalDate } from "../helper/dateHelpers";

/**
 * All functions that read data from the API (GET requests) should go here
 */

/**
 * Gets a the list of clinics available
 * @returns
 */
export async function getClinics(): Promise<ClinicList> {
  const url = "/api/v2/clinics";
  const response = await genericGetWithRetry<ClinicList>(url);

  return response.data;
}

/**
 * Gets paginated list of patients with filtering and sorting options
 * @param params - Pagination and filter parameters
 * @returns Promise with paginated patient list
 */
export async function getPaginatedPatients(
  params: PaginatedPatientsParams,
): Promise<PaginatedPatientList> {
  const url = "/api/v2/auth/patient";

  const queryParams: Record<string, string | number | boolean> = {
    limit: params.limit,
    offset: params.offset,
  };

  // Add optional parameters if they exist
  if (params.sort) queryParams.sort = params.sort;
  if (params.order) queryParams.order = params.order;
  if (params.code_like) queryParams.code_like = params.code_like;
  if (params.code_must_equal !== undefined)
    queryParams.code_must_equal = params.code_must_equal;
  if (params.gender_equals) queryParams.gender_equals = params.gender_equals;
  if (params.date_of_birth_less_than)
    queryParams.date_of_birth_less_than = params.date_of_birth_less_than;
  if (params.date_of_birth_greater_than)
    queryParams.date_of_birth_greater_than = params.date_of_birth_greater_than;
  if (params.created_less_than)
    queryParams.created_less_than = params.created_less_than;
  if (params.created_greater_than)
    queryParams.created_greater_than = params.created_greater_than;
  if (params.admission_less_than)
    queryParams.admission_less_than = params.admission_less_than;
  if (params.admission_greater_than)
    queryParams.admission_greater_than = params.admission_greater_than;
  if (params.discharge_less_than)
    queryParams.discharge_less_than = params.discharge_less_than;
  if (params.discharge_greater_than)
    queryParams.discharge_greater_than = params.discharge_greater_than;
  if (params.clinician_username_like)
    queryParams.clinician_username_like = params.clinician_username_like;
  if (params.clinician_username_must_equal !== undefined)
    queryParams.clinician_username_must_equal =
      params.clinician_username_must_equal;
  if (params.report_state) queryParams.report_state = params.report_state;
  // convert labels list to json format
  if (params.labels && params.labels.length > 0) {
    queryParams.labels = JSON.stringify(params.labels);
  }

  const response = await genericGetWithRetry<PaginatedPatientList>(url, {
    params: queryParams,
  });

  return response.data;
}

/**
 * Gets the list of clinicians when signed in as an admin.
 * @returns
 */
export async function getClinicians(): Promise<ClinicianList> {
  const url = "/api/v2/auth/clinician";
  const response = await genericGetWithRetry<ClinicianList>(url);

  return response.data;
}

/**
 * Gets the list of diagnoses
 * @returns
 */
export async function getDiagnoses(): Promise<DiagnosisListResponse> {
  const url = "/api/v2/auth/diagnoses";
  const response = await genericGetWithRetry<DiagnosisListResponse>(url);

  return response.data;
}

/**
 * Gets the clinician reports for the patient with the given patient code.
 * @param patientCode
 * @returns
 */
export async function getClinicianReportsForPatient(
  patientCode: string,
): Promise<ReportList> {
  //not sure if that is right, i just assume that that's what the api endpoint does
  // still confused, requesting report_facade.get_clinician_reports in the backend
  const url = "/api/v2/auth/patient/report";
  const response = await genericGetWithRetry<ReportList>(url, {
    params: { patient_code: patientCode },
  });

  return response.data;
}

/**
 * Gets the clinician report for the given survey session id. Only usable when signed in as clinician.
 * @param surveySessionId
 * @returns
 */
export async function getClinicianReport(
  surveySessionId: number,
): Promise<ClinicianReport> {
  const url = "/api/v2/auth/survey/report";
  const response = await genericGetWithRetry<ClinicianReport>(url, {
    params: { survey_session_id: surveySessionId },
  });

  return response.data;
}

/**
 * Gets a list of patient reports of the patient with the given id. Only usable as clinician.
 * This function can NOT be used when signed in as a patient. Use the respective function for patients instead!
 * @param patientCode
 * @returns
 */
export async function clinicianGetPatientReportsList(
  patientCode: string,
): Promise<ReportList> {
  //it looks like this is returning a list of all patient reports.
  //Then the client side has to check if the report for the survey session currently looked at
  //is part of that list
  //@rebecca why is it done like this instead of just using
  const url = "/api/v2/auth/patient/report";
  const response = await genericGetWithRetry<ReportList>(url, {
    params: {
      patient_code: patientCode,
      type: "patient",
    },
  });

  return response.data;
}

/**
 * Gets the list of survey time windows of the currently signed in patient. Only usable when signed in as patient.
 * @returns
 */
export async function patientGetSurveyTimeWindows(): Promise<SurveyTimeWindowForPatients> {
  const url = "/api/v2/auth/survey/windows";
  const response = await genericGetWithRetry<SurveyTimeWindowForPatients>(url);

  return response.data;
}

/**
 * Gets the list of survey time windows of the patient with the given patient code.
 * Not entirely sure for which roles this endpoint works. I suspect it works for the role clinician and admin.
 * @params patientCode
 * @returns
 */
export async function clinicianGetSurveyTimeWindows(
  patientCode: string,
): Promise<SurveyTimeWindow[]> {
  const url = "/api/v2/auth/survey/windows";
  const response = await genericGetWithRetry<SurveyTimeWindow[]>(url, {
    params: {
      patient_code: patientCode,
    },
  });

  return response.data;
}

/**
 * Gets the current survey time window of the currently signed in patient.
 * @returns
 */
export async function getCurrentSurveyTimeWindow(): Promise<SurveyTimeWindow> {
  const url = "/api/v2/auth/survey/windows/current";
  const response = await genericGetWithRetry<SurveyTimeWindow>(url);

  return response.data;
}

/**
 * Gets the list of patient reports when signed in as patient
 * @returns
 */
export async function getPatientReports(): Promise<ReportList> {
  const url = "/api/v2/auth/patient/report";
  const response = await genericGetWithRetry<ReportList>(url);

  return response.data;
}

/**
 * Gets the patient report with the given surveySessionId. Only usable when signed in as patient
 * @param surveySessionId
 * @returns
 */
export async function getPatientReport(
  surveySessionId: number,
): Promise<PatientReport> {
  const url = "/api/v2/auth/survey/report";
  const response = await genericGetWithRetry<PatientReport>(url, {
    params: { survey_session_id: surveySessionId },
  });

  return response.data;
}

/**
 * Gets the current survey items if available. Only usable when signed in as patient.
 * @returns
 */
export async function getSurvey(): Promise<SurveyResponse> {
  const url = "/api/v2/auth/survey/items";
  const response = await genericGetWithRetry<Survey>(url);

  return {
    statusCode: response.status,
    survey: response.data,
  };
}

/**
 * Gets the severity of the given survey session id
 * @param surveySessionId
 * @returns
 */
export async function getSeverity(surveySessionId: number): Promise<string> {
  const url = "/api/v2/auth/survey/severity";
  const response = await genericGetWithRetry<string>(url, {
    params: { survey_session_id: surveySessionId },
  });

  return response.data;
}

export async function getDiscardInfo(
  patientId: string,
  admissionDate: Date,
  dischargeDate: Date,
): Promise<SurveyDiscardInfo> {
  const url = "/api/v2/auth/patient/survey-discard-info";
  const response = await genericGetWithRetry<SurveyDiscardInfo>(url, {
    params: {
      patient_id: patientId,
      admission: formatLocalDate(admissionDate),
      discharge: formatLocalDate(dischargeDate),
    },
  });
  return response.data;
}

/**
 * Gets severities for multiple survey session ids at once. In the background it does multiple
 * calls to getSeverity in parallel to save time
 * @param surveySessionIds
 * @returns
 */
export async function getSeverities(
  surveySessionIds: number[],
): Promise<string[]> {
  const promises = surveySessionIds.map((surveySessionId) => {
    return getSeverity(surveySessionId);
  });
  const responses = await Promise.all(promises);
  return responses;
}

/**
 * This is a generic function used to get files/do http get requests..
 * If it gets a 403 status code the first time (=token invalid) it triggers
 * a renew of the token and tries to do the original request again. If that second try fails too then the
 * error is passed along. Other errors than status code 403 are always passed along.
 *
 * @param url
 * @param additionalParams optional object of additional parameters that will be passed to axios
 */
async function genericGetWithRetry<T>(
  url: string,
  additionalParams: AxiosRequestConfig = {},
): Promise<AxiosResponse<T>> {
  return await handleSessionExpiration(async () => {
    return await axios.get(url, {
      withCredentials: true,
      ...additionalParams,
    });
  });
}

interface ClinicList {
  clinics: Clinic[];
}

export interface Clinic {
  name: string;
  id: string;
  contact: string;
}

export interface PaginatedPatientsParams {
  limit: number;
  offset: number;
  sort?: string;
  order?: string;
  code_like?: string;
  code_must_equal?: boolean;
  gender_equals?: string;
  date_of_birth_less_than?: string;
  date_of_birth_greater_than?: string;
  created_less_than?: string;
  created_greater_than?: string;
  admission_less_than?: string;
  admission_greater_than?: string;
  discharge_less_than?: string;
  discharge_greater_than?: string;
  clinician_username_like?: string;
  clinician_username_must_equal?: boolean;
  labels?: { key: string; value: string }[];
  report_state?: PatientReportState;
}

export interface PaginatedPatientList {
  patient_information_list: Patient[];
  total_pages: number;
  total_elements: number;
}

export type PatientReportState =
  "NO_REPORT" | "UNREAD_REPORT" | "ALL_REPORT_READ";

export interface Patient {
  viewed: string;
  duplicate: boolean;
  date_of_birth: string;
  admission: string;
  discharge: string;
  last_update: number;
  code: string;
  clinician_username: string;
  clinician_id: string;
  diagnoses: Diagnosis[];
  gender: string; //maybe reduce to the possible values
  id: string;
  survey_statuses: SurveyState[];
  survey_time_windows: SurveyTimeWindow[];
  labels: Labels;
  report_state?: PatientReportState;
}

interface ClinicianList {
  clinicians: Clinician[];
}

export interface Clinician {
  id: string;
  username: string;
  enabled: boolean;
  password: string;
  edit: React.ReactElement;
  patientCount: number;
}

export interface Diagnosis {
  description: string;
  id: string;
}

export interface DiagnosisListResponse {
  diagnoses: Diagnosis[];
}

type ClinicianReport = string;

interface SurveyTimeWindowForPatients {
  stws: SurveyTimeWindow[];
}

export interface SurveyDiscardInfo {
  surveys: {
    id: number;
    stw_type: string;
    started: string;
    updated: string;
    finished: boolean;
    discarded: boolean;
  }[];
}

type PatientReport = string;
